import { useState, type ReactNode } from "react"
import { cn } from "@/lib/cn"

// Disabled-but-explained CTA. Used wherever a primary action is hidden
// behind a feature flag / invite-only beta — renders as a faded primary
// button with a tooltip body that surfaces the gating reason on hover or
// keyboard focus. Stays a real <button type="button" disabled> so it
// participates in tab order and screen-reader text without ever firing
// a click.
//
// Single prop is `reason` — the explanation tooltip body. The button
// label is passed through children so callers can match the CTA copy
// they would have used had the action been enabled.
type Props = {
  children: ReactNode
  reason: string
  className?: string
}

export function ComingSoonButton({ children, reason, className }: Props) {
  const [open, setOpen] = useState(false)
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        disabled
        aria-disabled="true"
        // Tooltip toggles via the wrapper's hover/focus events — the
        // inner button stays semantically disabled so click never fires.
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={(e) => { if (e.target.matches(":focus-visible")) setOpen(true) }}
        onBlur={() => setOpen(false)}
        className={cn(
          "inline-flex cursor-not-allowed items-center justify-center rounded-control bg-brand-red px-4 py-2 text-sm font-medium text-white opacity-50 shadow-redGlow transition",
          className,
        )}
      >
        {children}
      </button>
      {open && (
        <span
          role="tooltip"
          className={cn(
            "pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 block w-[260px] -translate-x-1/2",
            "rounded-card border border-border bg-app-panel2 px-3 py-2 text-xs font-normal normal-case tracking-normal leading-relaxed text-app-text shadow-lg",
          )}
        >
          {reason}
        </span>
      )}
    </span>
  )
}
